import { STATUSPAGE } from '@repo/ui';
import {
  ChangePasswordProfileUseCaseInterface,
  GetByIdProfileUseCaseInterface,
  UpdateProfileUseCaseInterface,
} from 'domain/usecases';
import { useFormik } from 'formik';
import { notifyError, notifySuccess } from 'presentation/app/components/notify';
import { useAuth } from 'presentation/app/hooks/useAuth';
import { useService } from 'presentation/app/hooks/useService';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import { loadProfile } from 'presentation/config/store/person/profile/profile-slice';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';

export const userProfile = () => {
  // hooks
  const dispatch = useDispatch();
  const { personService } = useService();
  const { profileList } = useAppSelector((state) => state.Profile);
  const { currentUser, changeDataProfileCookies } = useAuth();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const handleToggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  // validation
  const validationModalChangePassword = useFormik({
    enableReinitialize: true,
    initialValues: {
      password: '',
      confirmPassword: '',
    },

    validationSchema: Yup.object().shape({
      password: Yup.string()
        .required('Senha é obrigatória')
        .min(6, 'Senha deve ter no mínimo 6 caracteres'),
      confirmPassword: Yup.string()
        .required('Confirmação de senha é obrigatória')
        .oneOf([Yup.ref('password'), null], 'Senhas não conferem'),
    }),

    onSubmit: (values) => {
      requestChangePasswordProfile({ ...values, id: currentUser.id });
    },
  });

  const [loadingUpdateProfile, setLoadingUpdateProfile] =
    useState<STATUSPAGE>();

  const [loadingModalChangePassword, setLoadingModalChangePassword] =
    useState<STATUSPAGE>();

  // functions
  const clearModalAndClose = () => {
    validationModalChangePassword.resetForm();
    handleToggleModal();
  };

  const requestUpdateProfile = useCallback(
    async (params: UpdateProfileUseCaseInterface.Params) => {
      setLoadingUpdateProfile(STATUSPAGE.LOADING);
      return personService
        .updateProfile(params)
        .then((res) => {
          notifySuccess('Perfil atualizado com sucesso.');
          requestGetByIdProfile({ id: currentUser.id });
        })
        .catch((err) => {
          notifyError('Erro ao atualizar perfil.');
        })
        .finally(() => {
          setLoadingUpdateProfile(STATUSPAGE.DONE);
        });
    },
    [],
  );
  const requestGetByIdProfile = useCallback(
    async (params: GetByIdProfileUseCaseInterface.Params) => {
      return personService
        .getByIdProfile(params)
        .then((res: GetByIdProfileUseCaseInterface.Result) => {
          dispatch(loadProfile(res));
          changeDataProfileCookies({
            photo: res.photo,
          });
        })
        .catch((err) => {
          notifyError(err.message || 'Erro ao buscar perfil.');
        });
    },
    [],
  );

  const requestChangePasswordProfile = useCallback(
    async (params: ChangePasswordProfileUseCaseInterface.Params) => {
      setLoadingModalChangePassword(STATUSPAGE.LOADING);
      return personService
        .changePasswordProfile(params)
        .then((res) => {
          notifySuccess('Senha atualizado com sucesso.');
          clearModalAndClose();
        })
        .catch((err) => {
          notifyError('Erro ao atualizar senha.');
        })
        .finally(() => {
          setLoadingModalChangePassword(STATUSPAGE.DONE);
        });
    },
    [handleToggleModal],
  );

  return {
    handleToggleModal,
    clearModalAndClose,
    requestUpdateProfile,
    requestGetByIdProfile,
    requestChangePasswordProfile,
    profileList,
    isModalOpen,
    loadingUpdateProfile,
    loadingModalChangePassword,
    validationModalChangePassword,
  };
};
